import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, useToggle } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { ArrowUp } from '@factorialco/f0-react/icons/app'
import {
  BankingValueObjectsPaymentRequestStatusEnum,
  PaymentRequestFieldsFragment,
  PaymentRequestsDataCollectionDocument,
  PaymentRequestsPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { PaymentsOptionItem } from 'modules/banking/components/AddPaymentRequestsToPaymentForm'
import { PayPaymentRequestsInBulkModal } from 'modules/banking/components/PayPaymentRequestsInBulkModal'
import { useEmployeesFilter } from 'modules/employees/lib/filters/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const FEATURE_FLAG_NAME = 'dev_banking_payment_requests'

type StatusColor = 'neutral' | 'info' | 'positive' | 'warning'

const getStatusColor = (status: BankingValueObjectsPaymentRequestStatusEnum): StatusColor => {
  const statusColorMap: Record<BankingValueObjectsPaymentRequestStatusEnum, StatusColor> = {
    init: 'neutral',
    in_payment: 'warning',
    in_progress: 'info',
    paid: 'positive',
  }
  return statusColorMap[status] ?? 'neutral'
}

const getStatusLabel = (status: BankingValueObjectsPaymentRequestStatusEnum): string => {
  const statusLabelMap: Record<BankingValueObjectsPaymentRequestStatusEnum, string> = {
    init: i18n.t('banking.paymentRequests.status.init'),
    in_payment: i18n.t('banking.paymentRequests.status.inPayment'),
    in_progress: i18n.t('banking.paymentRequests.status.inProgress'),
    paid: i18n.t('banking.paymentRequests.status.paid'),
  }
  return statusLabelMap[status] ?? status
}

const PaymentRequestsPage = () => {
  const {
    access: { locale },
    company,
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [sendToPayModalOpen, toggleSendToPayModal] = useToggle(false)

  const { data, loading, error } = useQuery(PaymentRequestsPageDocument, {
    variables: {
      companyId: company.id,
      featureNames: [FEATURE_FLAG_NAME],
    },
  })

  const isFeatureEnabled =
    data?.features?.featuresConnection?.nodes.find(
      (feature: { name: string; enabled: boolean }) => feature.name === FEATURE_FLAG_NAME
    )?.enabled ?? false

  const paymentsOptions: PaymentsOptionItem[] =
    data?.banking?.payments?.flatMap((payment) => {
      return { label: payment.label, value: payment.id }
    }) || []

  const requesterFilter = useEmployeesFilter({
    label: i18n.t('banking.paymentRequests.filters.requester'),
  })

  const goToPayments = () => navigate(navigation.finance.banking.payments.fullPath())

  const dataSource = useGraphqlDataCollectionSource({
    document: PaymentRequestsDataCollectionDocument,
    mapData: (data) =>
      data.banking.paymentRequestsConnection.edges.map(
        (edge: { node: PaymentRequestFieldsFragment }) => edge.node
      ),
    mapVariables: ({ filters }) => ({
      requesterIds: filters.requester?.length ? filters.requester : undefined,
    }),
    paginationType: 'pages',
    perPage: 10,
    mapPagination: (data) => ({
      total: data.banking.paymentRequestsConnection.totalCount ?? 0,
      hasNextPage: data.banking.paymentRequestsConnection.pageInfo.hasNextPage ?? false,
      cursor: data.banking.paymentRequestsConnection.pageInfo.endCursor ?? undefined,
    }),
    filters: {
      requester: requesterFilter,
    },
    itemUrl: (item: PaymentRequestFieldsFragment) =>
      navigation.finance.treasury.banking.payments.paymentRequests.paymentRequestId.fullPath({
        paymentRequestId: item.id,
      }),
    selectable: (item: PaymentRequestFieldsFragment) =>
      item.status === 'init' ? String(item.id) : undefined,
    itemActions: (item: PaymentRequestFieldsFragment) =>
      item.status === 'init'
        ? [
            {
              label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
              icon: ArrowUp,
              type: 'primary',
              onClick: () => {
                setSelectedIds([item.id])
                toggleSendToPayModal(true)()
              },
            },
          ]
        : undefined,
    bulkActions: () => ({
      primary: [
        {
          id: 'send-to-pay',
          label: i18n.t('banking.paymentRequests.actions.send_to_pay'),
        },
      ],
      secondary: [],
    }),
  })

  const handleBulkAction = (
    action: string,
    items: { itemsStatus: readonly { checked: boolean; item: PaymentRequestFieldsFragment }[] }
  ) => {
    const selectedItems = items.itemsStatus.filter((item) => item.checked).map((item) => item.item)
    const ids = selectedItems.map((item) => item.id)

    if (action === 'send-to-pay') {
      setSelectedIds(ids)
      toggleSendToPayModal(true)()
    }
  }

  const columns = useMemo(
    () => [
      {
        label: i18n.t('banking.paymentRequests.columns.paymentRequest'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: row.label,
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.requester'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'person' as const,
          value: {
            firstName: row.requester?.access?.fullName ?? '-',
            lastName: '',
          },
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.amount'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'text' as const,
          value: MoneyWithCurrency({ amount: row.amountCents, currency: row.currency, locale }),
        }),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.dueDate'),
        render: (row: PaymentRequestFieldsFragment) =>
          convertDate(row.dueDate, 'backend_datetime', 'ui_abbreviated_month_year'),
      },
      {
        label: i18n.t('banking.paymentRequests.columns.status'),
        render: (row: PaymentRequestFieldsFragment) => ({
          type: 'status' as const,
          value: {
            label: getStatusLabel(row.status),
            status: getStatusColor(row.status),
          },
        }),
      },
    ],
    [locale]
  )

  const visualizations = useMemo(
    () => [
      {
        type: 'table' as const,
        options: {
          columns,
        },
      },
    ],
    [columns]
  )

  return (
    <ResponsivePage.Body
      error={error}
      loading={loading}
      authorized={isFeatureEnabled}
      variant="full"
    >
      <Box flexDirection="column" gap="s16" width="full" height="full">
        <OneDataCollection
          source={dataSource}
          visualizations={visualizations}
          onBulkAction={handleBulkAction}
        />
      </Box>
      {sendToPayModalOpen && (
        <PayPaymentRequestsInBulkModal
          onSuccess={goToPayments}
          onClose={() => toggleSendToPayModal(false)()}
          paymentRequestIds={selectedIds}
          paymentsOptions={paymentsOptions}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default PaymentRequestsPage
